import { Head, router, usePage } from '@inertiajs/react';
import { motion } from 'motion/react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Container } from '@/theme1/components/common/Container';
import Breadcrumbs from '@/theme1/components/product/Breadcrumbs';
import ProductGallery from '@/theme1/components/product/ProductGallery';
import ProductInfo from '@/theme1/components/product/ProductInfo';
import ProductAccordion from '@/theme1/components/product/ProductAccordion';
import ProductSpecifications from '@/theme1/components/product/ProductSpecifications';
import ProductFeatures from '@/theme1/components/product/ProductFeatures';
import StickyPurchaseBar from '@/theme1/components/product/StickyPurchaseBar';
import RelatedProducts from '@/theme1/components/recommendations/RelatedProducts';
import RecentlyViewed from '@/theme1/components/recommendations/RecentlyViewed';

import { useCart } from '@/theme1/hooks/useCart';
import { useWishlist } from '@/theme1/hooks/useWishlist';

export default function ProductDetails() {
    const {
        slug,
        product: pageProduct = null,
        relatedProducts: pageRelatedProducts = [],
        recentlyViewedProducts: pageRecentlyViewedProducts = [],
    } = usePage().props;

    const [selectedColor, setSelectedColor] = useState('');
    const [selectedSize, setSelectedSize] = useState('');
    const [quantity, setQuantity] = useState(1);

    const { add } = useCart();
    const { isWishlisted, toggleWishlist } = useWishlist();

    const product = useMemo(() => {
        const sourceProduct = pageProduct;
        if (!sourceProduct) return null;
        return normalizeProductData(sourceProduct);
    }, [pageProduct]);

    const relatedProducts = useMemo(() => {
        return pageRelatedProducts.map(normalizeProductCardData);
    }, [pageRelatedProducts]);

    const recentlyViewedProducts = useMemo(() => {
        return pageRecentlyViewedProducts.map(normalizeProductCardData);
    }, [pageRecentlyViewedProducts]);

    const activeColor = selectedColor || product?.colors?.[0]?.name || '';

    const activeSize =
        selectedSize || product?.sizes?.[1] || product?.sizes?.[0] || '';

    const wished = product ? isWishlisted(product.slug) : false;

    const validateSelection = useCallback(() => {
        if (!product) return false;

        if (product.isSoldOut || product.stock <= 0) {
            toast.error('This product is currently sold out.');
            return false;
        }

        if (product.colors?.length > 0 && !activeColor) {
            toast.error('Please select a color.');
            return false;
        }

        if (product.sizes?.length > 0 && !activeSize) {
            toast.error('Please select a size.');
            return false;
        }

        if (!Number.isFinite(Number(quantity)) || Number(quantity) < 1) {
            toast.error('Please select a valid quantity.');
            return false;
        }

        return true;
    }, [product, activeColor, activeSize, quantity]);

    const getCartProduct = useCallback(() => {
        if (!product) return null;

        return {
            ...product,

            id: product.id,
            slug: product.slug,
            title: product.title,
            price: Number(product.price || 0),

            image: product.image || product.gallery?.[0]?.src || null,

            series: product.series,
            discount: product.discount,
        };
    }, [product]);

    const handleAddToCart = useCallback(() => {
        if (!validateSelection()) return;

        const cartProduct = getCartProduct();
        if (!cartProduct) return;

        add(cartProduct, {
            quantity: Number(quantity),
            selectedColor: activeColor,
            selectedSize: activeSize,
        });

        toast.success(`${product.title} added to cart.`);
    }, [
        validateSelection,
        getCartProduct,
        add,
        quantity,
        activeColor,
        activeSize,
        product,
    ]);

    const handleBuyNow = useCallback(() => {
        if (!validateSelection()) return;

        const cartProduct = getCartProduct();
        if (!cartProduct) return;

        add(cartProduct, {
            quantity: Number(quantity),
            selectedColor: activeColor,
            selectedSize: activeSize,
        });

        router.visit('/checkout');
    }, [
        validateSelection,
        getCartProduct,
        add,
        quantity,
        activeColor,
        activeSize,
    ]);

    const handleWishlist = useCallback(() => {
        if (!product) return;

        toggleWishlist(product);

        if (isWishlisted(product.slug)) {
            toast.success(`${product.title} removed from wishlist.`);
        } else {
            toast.success(`${product.title} added to wishlist.`);
        }
    }, [product, toggleWishlist, isWishlisted]);

    if (!product) {
        return (
            <>
                <Head title="Product Not Found - Captex" />

                <Container className="py-24">
                    <div className="rounded-[2rem] border border-border bg-neutral-100/30 p-10 text-center">
                        <h1 className="text-3xl font-semibold">
                            Product not found
                        </h1>

                        <p className="mt-3 text-muted-foreground">
                            No product was found for this URL.
                        </p>
                    </div>
                </Container>
            </>
        );
    }

    const accordionItems = [
        {
            title: 'Product Details',
            content: (
                <ProductSpecifications
                    specifications={product.specifications}
                />
            ),
        },
        {
            title: 'Delivery & Payment',
            content: product.shippingInfo,
        },
        {
            title: 'Exchange Policy',
            content: product.returnPolicy,
        },
    ];

    return (
        <>
            <Head title={`${product.title} - Captex`}>
                <meta name="description" content={product.description} />
            </Head>

            <Container className="pt-6 pb-28 sm:pt-8 lg:pt-10 lg:pb-16">
                <div className="space-y-10">
                    <Breadcrumbs
                        items={[
                            {
                                label: 'Home',
                                href: '/',
                            },
                            {
                                label: 'Shop',
                                href: '/shop',
                            },
                            {
                                label: product.category || 'Collections',
                                href: product.collection
                                    ? `/collections/${product.collection}`
                                    : '/collections',
                            },
                            {
                                label: product.title,
                                href: `/products/${product.slug}`,
                            },
                        ]}
                    />

                    <motion.section
                        initial={{ opacity: 0, y: 26 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                            duration: 0.55,
                            ease: 'easeOut',
                        }}
                        className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-start"
                    >
                        <ProductGallery gallery={product.gallery} />

                        <ProductInfo
                            product={product}
                            selectedColor={activeColor}
                            selectedSize={activeSize}
                            quantity={quantity}
                            wished={wished}
                            onColorChange={setSelectedColor}
                            onSizeChange={setSelectedSize}
                            onQuantityChange={setQuantity}
                            onAddToCart={handleAddToCart}
                            onBuyNow={handleBuyNow}
                            onWishlist={handleWishlist}
                        />
                    </motion.section>

                    <ProductFeatures features={product.features} />

                    <section className="grid gap-6 rounded-[2rem] p-5 shadow-xl shadow-black/5 lg:grid-cols-[0.9fr_1.1fr] lg:p-8">
                        <div className="flex items-center gap-5">
                            <img
                                src={product.gallery?.[0]?.src || product.image}
                                alt={product.title}
                                className="size-32 rounded-2xl object-cover"
                            />

                            <div>
                                <h2 className="text-2xl font-black tracking-tight uppercase">
                                    Made for everyday confidence
                                </h2>

                                <p className="mt-3 text-sm leading-7 text-muted-foreground">
                                    {product.description}
                                </p>
                            </div>
                        </div>

                        <ProductAccordion items={accordionItems} />
                    </section>

                    <RelatedProducts products={relatedProducts} />

                    <RecentlyViewed products={recentlyViewedProducts} />
                </div>
            </Container>

            <StickyPurchaseBar
                product={product}
                wished={wished}
                onAddToCart={handleAddToCart}
                onBuyNow={handleBuyNow}
                onWishlist={handleWishlist}
            />
        </>
    );
}

function normalizeProductCardData(item) {
    const primaryImage = item.image || item.gallery?.[0]?.src || null;

    const secondaryImage =
        item.hoverImage || item.gallery?.[1]?.src || primaryImage;

    return {
        ...item,

        name: item.name || item.title,
        title: item.title || item.name,
        category: item.category || '',

        image: primaryImage,
        hoverImage: secondaryImage,

        comparePrice: item.comparePrice || item.compareAtPrice || null,

        compareAtPrice: item.compareAtPrice || item.comparePrice || null,

        gallery: item.gallery?.length
            ? item.gallery
            : [
                  {
                      type: 'image',
                      src: primaryImage,
                      alt: `${item.title} main view`,
                  },
                  {
                      type: 'image',
                      src: secondaryImage,
                      alt: `${item.title} alternate view`,
                  },
              ],

        images: item.gallery?.length
            ? item.gallery.map((media) => media.src)
            : [primaryImage, secondaryImage].filter(Boolean),

        badges: [
            item.isNew ? 'New' : null,
            item.isBestSeller ? 'Best Seller' : null,
            item.discount ? 'Sale' : null,
            item.isSoldOut ? 'Sold Out' : null,
        ].filter(Boolean),
    };
}

function normalizeProductData(sourceProduct, fallbackProduct = null) {
    const base = fallbackProduct || {};
    const primaryImage =
        sourceProduct.image ||
        sourceProduct.imageUrl ||
        sourceProduct.thumbnail ||
        sourceProduct.gallery?.[0]?.src ||
        sourceProduct.images?.[0]?.src ||
        sourceProduct.images?.[0] ||
        base.image ||
        base.imageUrl ||
        base.thumbnail ||
        base.gallery?.[0]?.src ||
        base.images?.[0]?.src ||
        base.images?.[0] ||
        null;

    const secondaryImage =
        sourceProduct.hoverImage ||
        sourceProduct.gallery?.[1]?.src ||
        sourceProduct.images?.[1]?.src ||
        sourceProduct.images?.[1] ||
        base.hoverImage ||
        base.gallery?.[1]?.src ||
        base.images?.[1]?.src ||
        base.images?.[1] ||
        primaryImage;

    const gallery =
        Array.isArray(sourceProduct.gallery) && sourceProduct.gallery.length
            ? sourceProduct.gallery
            : Array.isArray(base.gallery) && base.gallery.length
              ? base.gallery
              : [
                    {
                        type: 'image',
                        src: primaryImage,
                        alt: `${sourceProduct.title || sourceProduct.name || 'Product'} main view`,
                    },
                    {
                        type: 'image',
                        src: secondaryImage,
                        alt: `${sourceProduct.title || sourceProduct.name || 'Product'} alternate view`,
                    },
                ];

    const colors =
        Array.isArray(sourceProduct.colors) && sourceProduct.colors.length
            ? sourceProduct.colors.map(normalizeColor)
            : Array.isArray(base.colors) && base.colors.length
              ? base.colors.map(normalizeColor)
              : [];

    const sizes =
        Array.isArray(sourceProduct.sizes) && sourceProduct.sizes.length
            ? sourceProduct.sizes
            : Array.isArray(base.sizes) && base.sizes.length
              ? base.sizes
              : ['One Size'];

    return {
        ...base,
        ...sourceProduct,

        name:
            sourceProduct.name ||
            sourceProduct.title ||
            base.name ||
            base.title,
        title:
            sourceProduct.title ||
            sourceProduct.name ||
            base.title ||
            base.name,
        category: sourceProduct.category || base.category || '',
        collection:
            sourceProduct.collection ||
            base.collection ||
            slugify(sourceProduct.category || base.category || ''),
        description: sourceProduct.description || base.description || '',
        stock: sourceProduct.stock ?? base.stock ?? 0,
        SKU: sourceProduct.SKU || base.SKU || '',
        brand: sourceProduct.brand || base.brand || '',
        materials: sourceProduct.materials || base.materials || '',
        colors,
        sizes,
        gallery,
        images:
            sourceProduct.images ||
            base.images ||
            gallery.map((media) => media.src),
        specifications:
            sourceProduct.specifications || base.specifications || [],
        shippingInfo:
            sourceProduct.shippingInfo ||
            base.shippingInfo ||
            'Free delivery is available on selected orders.',
        returnPolicy:
            sourceProduct.returnPolicy ||
            base.returnPolicy ||
            'Easy 7-day return policy for eligible unused products.',
        careInstructions:
            sourceProduct.careInstructions ||
            base.careInstructions ||
            'Spot clean only. Do not bleach.',
        features: sourceProduct.features ||
            base.features || [
                'Premium structured silhouette',
                'Adjustable back closure',
                'Comfortable everyday fit',
            ],
        comparePrice:
            sourceProduct.comparePrice ??
            sourceProduct.compareAtPrice ??
            base.comparePrice ??
            base.compareAtPrice ??
            null,
        compareAtPrice:
            sourceProduct.compareAtPrice ??
            sourceProduct.comparePrice ??
            base.compareAtPrice ??
            base.comparePrice ??
            null,
    };
}

function normalizeColor(color) {
    return typeof color === 'string'
        ? {
              name: color,
              value: getColorValue(color),
          }
        : color;
}

function getColorValue(color) {
    const colors = {
        Black: '#111111',
        Red: '#b91c1c',
        Sand: '#d6c4a5',
        Cream: '#f3ead8',
        Navy: '#1f2a44',
        Brown: '#6b3f2a',
        Blue: '#2563eb',
        White: '#ffffff',
        Clay: '#b86b4b',
        Orange: '#f97316',
        Silver: '#c0c0c0',
        Beige: '#d8c4a3',
    };

    return colors[color] || '#111111';
}

function slugify(value = '') {
    const normalized = String(value).trim();

    if (
        !normalized ||
        normalized.toLowerCase() === 'undefined' ||
        normalized.toLowerCase() === 'null'
    ) {
        return '';
    }

    return normalized
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}
